import { Formik, FormikValues } from 'formik';
import { useCallback, useRef } from 'react';
import { useAlert } from 'components';

import { InputField } from 'common';
import { Icon } from 'common/icon';
import { IUser } from 'constants/types';
import { useClickOutside } from 'hooks/useClickOutside';
import { handleMutation } from 'utils/handleMutation';
import { ModalMain } from '../main';
import { initialValues, model, validationSchema } from './config';

import { useChangeEmailMutation } from 'hooks/api/users';

import { Button } from 'common/new-button';
import styles from './styles.module.css';
import { Analytics } from 'constants/analytics';

interface Props {
  data?: IUser;
  isVisible: boolean;
  onClose?: () => void;
}

export const EditEmailModal = ({ data, isVisible, onClose }: Props) => {
  const alert = useAlert();
  const modalRef = useRef<HTMLDivElement | null>(null);

  useClickOutside({ onClick: onClose, ref: modalRef });

  const [changeEmail, changeEmailMutation] = useChangeEmailMutation();

  const isLoadingUsername = changeEmailMutation.isLoading;

  const onSuccess = useCallback(() => {
    alert.success('Email change request sent');
  }, [alert]);

  const onError = useCallback(
    (error: any) => {
      alert.error(error?.data?.error || 'Error');
    },
    [alert]
  );

  handleMutation({ ...changeEmailMutation, onSuccess, onError });

  const onSubmit = async (values: FormikValues) => {
    const { newEmail } = values;
    await changeEmail({ newEmail });
    if (onClose) {
      onClose();
    }
  };

  return (
    <ModalMain
      width={260}
      title=""
      targetRef={modalRef}
      isVisible={isVisible}
      closeOnClick={onClose}
      footer=""
    >
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues(data)}
        validationSchema={() => validationSchema()}
      >
        {({ dirty, isSubmitting, submitForm }) => (
          <div className={styles.body}>
            <div className={styles.wrapper_header}>
              <span>{'Edit email'}</span>
              <div className={styles.iconClose} onClick={onClose}>
                <Icon.Close />
              </div>
            </div>
            <div className={styles.wrapper_content}>
              <div className={styles.wrapper_input}>
                <InputField {...model.newEmail} variant="editProfile" />
              </div>
              <div className={styles.submitButton}>
                <Button
                  id={Analytics.user.editEmail}
                  onClick={submitForm}
                  disabled={!dirty || isSubmitting}
                  text={isLoadingUsername ? 'Loading...' : 'Submit'}
                />
              </div>
              <div className={styles.cancelButton}>
                <Button text="Cancel" onClick={onClose} />
              </div>
            </div>
          </div>
        )}
      </Formik>
    </ModalMain>
  );
};
